"use client"

/**
 * Console panel for use inside flexlayout-react tabs.
 *
 * Renders log output with Output / Errors / Warnings filter tabs.
 * The flexlayout tab header handles the "Console" label and close button.
 */

import { useMemo, useState } from "react"
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react"
import type { TabNode } from "flexlayout-react"
import { useEditorStore } from "@/lib/editor-store"

interface DiagnosticLine {
  file: string
  line: number
  message: string
}

function parseDiagnostics(text: string): DiagnosticLine[] {
  const results: DiagnosticLine[] = []
  const re = /^(?:error|warning):\s+([\w./-]+\.tex):(\d+):\s+(.+)/i
  for (const raw of text.split("\n")) {
    const m = raw.match(re)
    if (m) results.push({ file: m[1], line: parseInt(m[2], 10), message: m[3].trim() })
  }
  return results
}

interface LogEntry {
  id: string
  type: "output" | "error" | "warning" | "success"
  message: string
  details?: string
  timestamp: string
}

interface ConsolePanelProps {
  node: TabNode
}

export function ConsolePanel({ node }: ConsolePanelProps) {
  const { state, openFileAtLine } = useEditorStore()
  const [activeTab, setActiveTab] = useState("output")

  const logs = useMemo<LogEntry[]>(
    () =>
      state.events.map((event) => ({
        id: event.id,
        type:
          event.level === "error"
            ? "error"
            : event.level === "warn"
              ? "warning"
              : event.level === "success"
                ? "success"
                : "output",
        message: event.message,
        details: event.details,
        timestamp: new Date(event.at).toLocaleTimeString(),
      })),
    [state.events],
  )

  const errorCount = logs.filter((l) => l.type === "error").length
  const warningCount = logs.filter((l) => l.type === "warning").length
  const successCount = logs.filter((l) => l.type === "success").length

  const tabs = [
    {
      id: "output",
      label: "Output",
      count: logs.length,
      Icon: Info,
      iconColor: "text-gray-400",
      badge: "bg-[var(--ed-status-neutral-badge-bg)] text-[var(--ed-console-row-text)]",
    },
    {
      id: "errors",
      label: "Errors",
      count: errorCount,
      Icon: AlertCircle,
      iconColor: "text-red-500",
      badge: "bg-[var(--ed-status-error-badge-bg)] text-[var(--ed-status-error-badge-text)]",
    },
    {
      id: "warnings",
      label: "Warnings",
      count: warningCount,
      Icon: AlertTriangle,
      iconColor: "text-yellow-500",
      badge: "bg-[var(--ed-status-warn-badge-bg)] text-[var(--ed-status-warn-badge-text)]",
    },
    {
      id: "success",
      label: "Success",
      count: successCount,
      Icon: CheckCircle2,
      iconColor: "text-green-500",
      badge: "bg-[var(--ed-status-success-badge-bg)] text-[var(--ed-status-success-badge-text)]",
    },
  ] as const

  // "Output" is the combined, unfiltered stream of every log type — Errors/Warnings/Success
  // narrow down to just their own type.
  const visibleLogs =
    activeTab === "output"
      ? logs
      : activeTab === "errors"
        ? logs.filter((l) => l.type === "error")
        : activeTab === "warnings"
          ? logs.filter((l) => l.type === "warning")
          : logs.filter((l) => l.type === "success")

  return (
    <div className="flex flex-col h-full bg-[var(--ed-console-bg)]">
      <div className="flex flex-col flex-1 min-h-0 m-2 overflow-hidden rounded-[8px] bg-[var(--ed-console-inner-bg)] border border-[var(--ed-console-border)]">
        {/* Row 1: Filter Tabs */}
        <div className="flex items-center gap-1 min-h-9 px-3 shrink-0 border-b border-[var(--ed-console-border)]">
          <div className="flex items-center gap-1 w-full h-full px-2 py-1.5 rounded">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  activeTab === tab.id
                    ? "bg-transparent border text-[var(--ed-console-tab-active-text)]"
                    : "text-[var(--ed-console-tab-text)] hover:text-[var(--ed-console-tab-hover-text)]"
                }`}
                style={
                  activeTab === tab.id
                    ? {
                        borderColor: "var(--ed-console-tab-active-border)",
                        borderWidth: "var(--ed-console-tab-active-border-width)",
                      }
                    : undefined
                }
              >
                <tab.Icon className={`h-3.5 w-3.5 shrink-0 ${tab.iconColor}`} />
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded text-xs font-semibold ${tab.badge}`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: Log Content */}
        <div className="console-scroll flex-1 min-h-0 overflow-y-auto font-mono text-xs bg-[var(--ed-console-bg)] mr-1">
          {visibleLogs.length === 0 ? (
            <div className="p-4 text-gray-500 text-center">
              No {activeTab} logs
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {visibleLogs.map((log) => {
                const isError   = log.type === "error"
                const isWarning = log.type === "warning"
                // Parse clickable file:line references from message + details
                const diagSource = `${log.message}\n${log.details ?? ""}`
                const diagnostics = (isError || isWarning) ? parseDiagnostics(diagSource) : []

                return (
                  <div
                    key={log.id}
                    className={`rounded px-2 py-1.5 border ${
                      isError
                        ? "bg-[var(--ed-status-error-bg)] text-[var(--ed-status-error-text)] border-[var(--ed-status-error-border)]"
                        : isWarning
                          ? "bg-[var(--ed-status-warn-bg)] text-[var(--ed-status-warn-text)] border-[var(--ed-status-warn-border)]"
                          : log.type === "success"
                            ? "bg-[var(--ed-status-success-bg)] text-[var(--ed-status-success-text)] border-[var(--ed-status-success-border)]"
                            : "text-[var(--ed-console-row-text)] border-[var(--ed-console-row-border)]"
                    }`}
                  >
                    {/* Header row: icon + message + timestamp */}
                    <div className="flex items-start gap-2">
                      {log.type === "output"  && <Info         className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-gray-400" />}
                      {isError                && <AlertCircle  className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
                      {isWarning              && <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
                      {log.type === "success" && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold break-words">{log.message}</span>
                      </div>
                      <span className="ml-auto opacity-50 pl-2 text-[10px] shrink-0">{log.timestamp}</span>
                    </div>

                    {/* Full details — shown as pre so newlines are preserved */}
                    {(isError || isWarning) && log.details && (
                      <pre className="mt-2 whitespace-pre-wrap break-words text-[10px] leading-relaxed rounded-md border border-current/25 bg-[var(--ed-console-code-bg)] px-2.5 py-2 opacity-90 font-mono">
                        {log.details}
                      </pre>
                    )}

                    {/* Clickable jump-to-line links */}
                    {diagnostics.length > 0 && (
                      <div className="mt-2 space-y-1 border-t border-current/20 pt-1.5">
                        <span className="text-[10px] opacity-60 font-medium">Jump to error:</span>
                        {diagnostics.map((d, i) => (
                          <button
                            key={i}
                            onClick={() => openFileAtLine(d.file, d.file, d.line)}
                            className="flex items-start gap-1.5 w-full text-left group"
                            title={`Open ${d.file} at line ${d.line}`}
                          >
                            <span className="text-[var(--ed-status-link-text)] underline underline-offset-2 shrink-0 group-hover:text-[var(--ed-status-link-hover)] font-mono">
                              {d.file}:{d.line}
                            </span>
                            <span className="break-words opacity-80">&mdash; {d.message}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
